/**
 * Photo preparation for Team Chat: re-encodes JPEG/PNG/WebP through a canvas, which drops all metadata
 * (EXIF GPS location, camera serial, etc.) and shrinks large phone photos. Everything happens in memory.
 */
export const PHOTO_MAX_SIDE = 1920;

const REENCODE = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']);

export async function prepareImage(file: File): Promise<File> {
  if (!REENCODE.has(file.type)) return file; // GIF keeps its animation; others are sent as files
  let bmp: ImageBitmap;
  try {
    bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return file; // the browser cannot decode it (e.g. HEIC outside Safari): send as an ordinary file
  }
  const scale = Math.min(1, PHOTO_MAX_SIDE / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * scale));
  const h = Math.max(1, Math.round(bmp.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const g = canvas.getContext('2d');
  if (!g) {
    bmp.close();
    return file;
  }
  g.fillStyle = '#ffffff'; // transparent PNGs become white, not black, as JPEG
  g.fillRect(0, 0, w, h);
  g.drawImage(bmp, 0, 0, w, h);
  bmp.close();
  const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', 0.86));
  canvas.width = canvas.height = 0;
  if (!blob) return file;
  const base = (file.name || 'foto').replace(/\.[^.]+$/, '') || 'foto';
  return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
}

export function canUseLiveCamera(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    window.isSecureContext &&
    // Phones/tablets open their own camera app through <input capture>, which is the better experience.
    !window.matchMedia('(pointer: coarse)').matches
  );
}

export function formatSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
